import React, { useState, useEffect, lazy, Suspense } from 'react';
import RequireAdmin from '../../routes/RequireAdmin';
import AdminHomeownersList from './AdminHomeownersList';
import AdminAddHomeowner from './AdminAddHomeowner';
import AdminAddContractor from './AdminAddContractor';
import AdminProjectsList from './AdminProjectsList';
import AdminContractorsList from './AdminContractorsList';
import AdminHomeownerView from './AdminHomeownerView';
import AdminContractorView from './AdminContractorView';
import ProjectCockpit from './ProjectCockpit';
// CB:BEGIN admin-detail-imports
import AdminContractorDetail from './AdminContractorDetail';
import AdminHomeownerDetail from './AdminHomeownerDetail';
import AdminEditHomeownerDashboard from './AdminEditHomeownerDashboard';
import AdminEditContractorDashboard from './AdminEditContractorDashboard';
import AdminMilestoneManagement from './AdminMilestoneManagement';
// CB:END admin-detail-imports
import AdminStatsCards from '../../components/admin/AdminStatsCards';
// CB:BEGIN lazy-load-kpi-tracker
// Lazy load KPITracker to avoid TDZ issues
const KPITracker = lazy(() => import('../../components/admin/KPITracker'));
// CB:END lazy-load-kpi-tracker
import ContractorVerificationWorkflow from '../../components/admin/ContractorVerificationWorkflow';
import ProjectPriorityManagement from '../../components/admin/ProjectPriorityManagement';
import QuestionnaireManagement from '../../components/admin/QuestionnaireManagement';
import LicenseVerificationWorkflow from '../../components/admin/LicenseVerificationWorkflow';
import InsuranceDocumentManagement from '../../components/admin/InsuranceDocumentManagement';
import PerformanceMonitoring from '../../components/admin/PerformanceMonitoring';
import StripeConnectStatusTracking from '../../components/admin/StripeConnectStatusTracking';
import RevenueKPIDashboard from '../../components/admin/RevenueKPIDashboard';
import AdminCreateQuotePage from './AdminCreateQuotePage';
import AdminQuoteDraftsPage from './AdminQuoteDraftsPage';
import AdminCreateInvoicePage from './AdminCreateInvoicePage';
import AdminFinancePage from './AdminFinancePage';
// CB:BEGIN operations-control-plane-ui
import AdminOperationsPage from './AdminOperationsPage';
// CB:END operations-control-plane-ui
import AdminOpsPage from './AdminOpsPage'; // CB:BEGIN admin-ops-page-import
// CB:END admin-ops-page-import
import ContractorPerformanceDashboard from '../../components/admin/ContractorPerformanceDashboard';
import ProjectCompletionDashboard from '../../components/admin/ProjectCompletionDashboard';
import CustomerSatisfactionDashboard from '../../components/admin/CustomerSatisfactionDashboard';
import SharedHeaderAdmin from '../../components/SharedHeaderAdmin';
import AdminManage from './AdminManage';
import AdminVerificationQueuePage from './AdminVerificationQueuePage';
import AdminDocumentsPage from './AdminDocumentsPage';
// CB:BEGIN admin-reviews-import
import AdminReviewsPage from './AdminReviewsPage';
// CB:END admin-reviews-import
// CB:BEGIN admin-alerts-panel-import
import AdminAlertsPanel from '../../components/admin/AdminAlertsPanel';
// CB:END admin-alerts-panel-import
// ImpersonationBanner is mounted globally in main.tsx — do not mount it again here
// CB:BEGIN admin-war-room-import
import AdminHelpPage from './AdminHelpPage';
// CB:END admin-war-room-import
// CB:BEGIN admin-support-requests-import
import AdminSupportRequestsPage from './AdminSupportRequestsPage';
// CB:END admin-support-requests-import
// CB:BEGIN admin-pm-import
import AdminPropertyManagersPage from './AdminPropertyManagersPage';
// CB:END admin-pm-import
// CB:BEGIN admin-realtors-import
import AdminRealtorsPage from './AdminRealtorsPage';
// CB:END admin-realtors-import
// CB:BEGIN admin-alerts-import
import AdminAlertsPage from './AdminAlertsPage';
// CB:END admin-alerts-import
// CB:BEGIN admin-health-cards-import
import AdminHealthCards from '../../components/admin/AdminHealthCards';
// CB:END admin-health-cards-import
// CB:BEGIN admin-command-center-import
import AdminCommandCenter from '../../components/admin/AdminCommandCenter';
// CB:END admin-command-center-import
import AdminDebugPanel from './AdminDebugPanel';
// CB:BEGIN admin-dispute-center-import
import AdminDisputeCenter from './AdminDisputeCenter';
// CB:END admin-dispute-center-import
// CB:BEGIN admin-marketplace-health-import
import AdminMarketplaceHealth from '../../components/admin/AdminMarketplaceHealth';
// CB:END admin-marketplace-health-import

interface AdminDashboardProps {
  currentScreen?: string;
  onNavigate?: (screen: string) => void;
}

const AdminDashboard: React.FC<AdminDashboardProps> = ({ currentScreen = 'admin-dashboard', onNavigate }) => {
  // CB:BEGIN dashboard-initial-loading
  const [initialLoading, setInitialLoading] = useState(true);
  // CB:END dashboard-initial-loading
  
  // CB:BEGIN dashboard-initial-loading-timer
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 600); // Short delay so RequireAdmin can resolve; was 3000
    return () => clearTimeout(timer);
  }, []);
  // CB:END dashboard-initial-loading-timer
  
  // Extract URL parameters for detail routes
  const urlParams = new URLSearchParams(window.location.search);
  const pathParams = window.location.pathname.match(/\/admin\/([^/]+)\/([^/]+)/);
  
  const renderContent = () => {
    // Handle admin privilege views
    if (currentScreen?.startsWith('admin-homeowner-view-')) {
      const homeownerId = currentScreen.replace('admin-homeowner-view-', '');
      return <AdminHomeownerView homeownerId={homeownerId} onNavigate={onNavigate || (() => {})} />;
    }
    
    if (currentScreen?.startsWith('admin-contractor-view-')) {
      const contractorId = currentScreen.replace('admin-contractor-view-', '');
      return <AdminContractorView contractorId={contractorId} onNavigate={onNavigate || (() => {})} />;
    }

    // Handle admin edit routes — renders the full-edit dashboard for each user type
    if (currentScreen?.startsWith('admin-homeowner-edit-')) {
      const homeownerId = currentScreen.replace('admin-homeowner-edit-', '');
      return <AdminEditHomeownerDashboard homeownerId={homeownerId} onNavigate={onNavigate || (() => {})} />;
    }

    if (currentScreen?.startsWith('admin-contractor-edit-')) {
      const contractorId = currentScreen.replace('admin-contractor-edit-', '');
      return <AdminEditContractorDashboard contractorId={contractorId} onNavigate={onNavigate || (() => {})} />;
    }

    if (currentScreen?.startsWith('admin-project-edit-')) {
      const projectId = currentScreen.replace('admin-project-edit-', '');
      return <ProjectCockpit projectId={projectId} onNavigate={onNavigate || (() => {})} />;
    }

    // CB:BEGIN admin-detail-routes
    if (currentScreen?.startsWith('admin-contractor-detail/')) {
      const contractorId = currentScreen.replace('admin-contractor-detail/', '');
      return <AdminContractorDetail contractorId={contractorId} onNavigate={onNavigate || (() => {})} />;
    }
    
    if (currentScreen?.startsWith('admin-homeowner-detail/')) {
      const homeownerId = currentScreen.replace('admin-homeowner-detail/', '');
      return <AdminHomeownerDetail homeownerId={homeownerId} onNavigate={onNavigate || (() => {})} />;
    }
    
    if (currentScreen?.startsWith('admin-milestone-management/')) {
      const projectId = currentScreen.replace('admin-milestone-management/', '');
      return <AdminMilestoneManagement projectId={projectId} onNavigate={onNavigate || (() => {})} />;
    }
    // CB:END admin-detail-routes

    switch (currentScreen) {
      case 'admin-homeowners-list':
        return <AdminHomeownersList onNavigate={onNavigate} />;
      case 'admin-homeowner-add':
        return <AdminAddHomeowner onNavigate={onNavigate} />;
      case 'admin-homeowner-detail':
        return <AdminHomeownerDetail homeownerId={pathParams?.[1] || urlParams.get('homeownerId') || ''} onNavigate={onNavigate || (() => {})} />;
      case 'admin-contractors-list':
        return <AdminContractorsList onNavigate={onNavigate} />;
      case 'admin-contractor-add':
        return <AdminAddContractor onNavigate={onNavigate} />;
      case 'admin-contractor-detail':
        return <AdminContractorDetail contractorId={pathParams?.[1] || urlParams.get('contractorId') || ''} onNavigate={onNavigate || (() => {})} />;
      case 'admin-edit-homeowner-dashboard':
        return <AdminEditHomeownerDashboard homeownerId={pathParams?.[1] || urlParams.get('homeownerId') || ''} onNavigate={onNavigate || (() => {})} />;
      case 'admin-edit-contractor-dashboard':
        return <AdminEditContractorDashboard contractorId={pathParams?.[1] || urlParams.get('contractorId') || ''} onNavigate={onNavigate || (() => {})} />;
      case 'admin-project-cockpit':
        return <ProjectCockpit />;
             case 'admin-projects-list':
               return <AdminProjectsList onNavigate={onNavigate} />;
      case 'admin-kpi-tracker':
        return (
          <Suspense fallback={<div className="flex items-center justify-center h-64">Loading KPI Tracker...</div>}>
            <KPITracker onNavigate={onNavigate} />
          </Suspense>
        );
      case 'admin-contractor-verification':
        return <ContractorVerificationWorkflow />;
      case 'admin-verification-queue':
        return <AdminVerificationQueuePage onNavigate={onNavigate} />;
      case 'admin-documents':
        return <AdminDocumentsPage onNavigate={onNavigate} />;
      case 'admin-project-priority':
        return <ProjectPriorityManagement />;
      case 'admin-questionnaire-management':
        return <QuestionnaireManagement />;
      case 'admin-license-verification':
        return <LicenseVerificationWorkflow />;
      case 'admin-insurance-management':
        return <InsuranceDocumentManagement />;
      case 'admin-performance-monitoring':
        return <PerformanceMonitoring />;
      case 'admin-stripe-status-tracking':
        return <StripeConnectStatusTracking />;
      case 'admin-revenue-kpi':
        return <RevenueKPIDashboard onNavigate={onNavigate} />;
      case 'admin-quotes-new':
        return <AdminCreateQuotePage onNavigate={onNavigate} />;
      case 'admin-quotes-drafts':
        return <AdminQuoteDraftsPage onNavigate={onNavigate} />;
      case 'admin-invoices-create':
        return <AdminCreateInvoicePage onNavigate={onNavigate} />;
      case 'admin-finance':
        return <AdminFinancePage onNavigate={onNavigate} />;
      // CB:BEGIN operations-control-plane-ui
      case 'admin-operations':
        return <AdminOperationsPage />;
      // CB:END operations-control-plane-ui
      case 'admin-ops':
        return <AdminOpsPage onNavigate={onNavigate} />;
      case 'admin-contractor-performance':
        return <ContractorPerformanceDashboard />;
      case 'admin-project-completion':
        return <ProjectCompletionDashboard />;
      case 'admin-customer-satisfaction':
        return <CustomerSatisfactionDashboard />;
      case 'admin-manage':
        return <AdminManage />;
      // CB:BEGIN admin-reviews-route
      case 'admin-reviews':
        return <AdminReviewsPage />;
      // CB:END admin-reviews-route
      // CB:BEGIN admin-help-route
      case 'admin-help':
        return <AdminHelpPage />;
      // CB:END admin-help-route
      // CB:BEGIN admin-support-requests-route
      case 'admin-support-requests':
        return <AdminSupportRequestsPage onNavigate={onNavigate} />;
      // CB:END admin-support-requests-route
      // CB:BEGIN admin-pm-route
      case 'admin-property-managers':
        return <AdminPropertyManagersPage onNavigate={onNavigate} />;
      // CB:END admin-pm-route
      // CB:BEGIN admin-realtors-route
      case 'admin-realtors':
        return <AdminRealtorsPage onNavigate={onNavigate} />;
      case 'admin-quote-requests':
        return <AdminRealtorsPage onNavigate={onNavigate} initialTab="quotes" />;
      // CB:END admin-realtors-route
      // CB:BEGIN admin-alerts-route
      case 'admin-alerts':
        return <AdminAlertsPage onNavigate={onNavigate} />;
      // CB:END admin-alerts-route
      // CB:BEGIN admin-dispute-center-route
      case 'admin-disputes':
        return <AdminDisputeCenter onNavigate={onNavigate} />;
      // CB:END admin-dispute-center-route
      // CB:BEGIN admin-marketplace-health-route
      case 'admin-marketplace-health':
        return <AdminMarketplaceHealth />;
      // CB:END admin-marketplace-health-route
      case 'admin-debug':
        return (
          <div style={{ padding: 32, maxWidth: 800, margin: '0 auto' }}>
            <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 24 }}>Admin Debug Info</h1>
            <AdminDebugPanel />
          </div>
        );
      default:
        return (
          <div>
            {/* CB:BEGIN admin-command-center */}
            <AdminCommandCenter onNavigate={onNavigate} />
            {/* CB:END admin-command-center */}
            {/* Legacy anchors — kept so existing CB tags resolve cleanly */}
            {/* CB:BEGIN admin-alerts-panel */}{/* CB:END admin-alerts-panel */}
            {/* CB:BEGIN admin-health-cards-section */}{/* CB:END admin-health-cards-section */}
          </div>
        );
    }
  };

  // CB:BEGIN dashboard-initial-loading-screen
  if (initialLoading) {
    return (
      <RequireAdmin>
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
          <SharedHeaderAdmin onNavigate={onNavigate || (() => {})} />
          <div className="content" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '16px' }}>Loading dashboard</p>
          </div>
        </div>
      </RequireAdmin>
    );
  }
  // CB:END dashboard-initial-loading-screen

  return (
    <RequireAdmin>
      {/* ImpersonationBanner is rendered globally in main.tsx */}
      <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
        {/* Always show the admin header */}
        <SharedHeaderAdmin onNavigate={onNavigate || (() => {})} />

        {/* Main content */}
        <div className="content">
          {renderContent()}
        </div>
      </div>
    </RequireAdmin>
  );
};

export default AdminDashboard;
// CB:END admin-dashboard-main
